import { ReportScene, reportScene } from "../services/DataReporter";
import { findLast, isInEnum } from "../utils/array";
import { getFileType } from "../utils/file";
import { tryParse } from "../utils/object";
import { sanitizeMessage, useMessageStore } from "./message";
import {
  CurrentChatStatus,
  Message,
  MessageSender,
  MessageStatus,
  MessageType,
  RiskStatus,
  SocketConnectionStatus,
} from "./types";

// 人工欢迎语
export const isAgentWelcome = (msg: Message) =>
  msg.type === MessageType.Welcome && msg.senderType === MessageSender.SYSTEM;

// 排队消息
export const isLineUpMsg = (msg: Message) =>
  msg?.type === MessageType.LineUp && msg.senderType === MessageSender.SYSTEM;

export const isChatEndMsg = (msg: Message) =>
  msg?.type === MessageType.MsgEnd && msg.senderType === MessageSender.SYSTEM;

export const isAgentOnline = (msg: Message) =>
  msg.senderType === MessageSender.AGENT;

export const isWithdrawnMsg = (msg: Message) =>
  msg.type === MessageType.Recall || !!msg.beRecalled;

export const isForbiddenMsg = (msg: Message) =>
  msg.type === MessageType.ForbiddenMsg || msg.riskStatus === RiskStatus.Failed;

export const formatHistory = (historyData: Message[]) => {
  return historyData.filter((item) => {
    const parseContent = tryParse(item.content);
    if (parseContent && parseContent.fileName) {
      item.content = JSON.stringify({
        ...parseContent,
        fileType: getFileType(parseContent.fileName),
      });
    }
    return item.type !== MessageType.LineUp;
  });
};

export const isDisConnectedNeedReconnect = () => {
  const { chatStatus, isConnected } = useMessageStore.getState();
  return isConnected === SocketConnectionStatus.CLOSE && chatStatus;
};

export const formatMsgList = (msgList: Message[], newMessage: Message) => {
  if (
    isLineUpMsg(newMessage) ||
    isAgentOnline(newMessage) ||
    isChatEndMsg(newMessage) ||
    isAgentWelcome(newMessage)
  ) {
    // 来源的消息是排队消息, 隐藏之前的排队消息，插入新的排队消息
    const { index } =
      findLast(
        msgList,
        (msg) =>
          msg?.type === MessageType.LineUp &&
          msg.senderType === MessageSender.SYSTEM,
      ) || {};
    if (index !== undefined) {
      msgList[index].hide = true;
    }
  }

  // 如果是撤回消息，更新本地消息状态
  if (
    isWithdrawnMsg(newMessage) &&
    newMessage.senderType === MessageSender.SYSTEM
  ) {
    // 更新本地状态
    useMessageStore
      .getState()
      .withdrawMessage(newMessage.id as string, MessageSender.SYSTEM);
  }

  // 文件消息类型，装换一下
  const parseContent = tryParse(newMessage.content);
  if (parseContent && parseContent.fileName) {
    newMessage.content = JSON.stringify({
      ...parseContent,
      fileType: getFileType(parseContent.fileName),
    });
  }

  return [
    ...msgList,
    {
      ...sanitizeMessage(newMessage),
      status: MessageStatus.Success, // 接收的消息都是成功状态
    },
  ];
};

export const getCurrentChatStatus = (newMessage: Message) => {
  if (isLineUpMsg(newMessage)) {
    return CurrentChatStatus.LineUp;
  }

  if (isAgentOnline(newMessage) || isAgentWelcome(newMessage)) {
    return CurrentChatStatus.Online;
  }

  if (isChatEndMsg(newMessage)) {
    return CurrentChatStatus.ChatEnd;
  }

  return null;
};

export const handleLineMsg = (msgList: Message[], newMessage: Message) => {
  let lineMsg = null;
  const excludeLineMsgList: Message[] = [];
  msgList.forEach((msg) => {
    if (isLineUpMsg(msg)) {
      lineMsg = msg;
    } else {
      excludeLineMsgList.push(msg);
    }
  });
  return {
    excludeLineMsgList,
    appendMsg: lineMsg ? [newMessage, lineMsg] : [newMessage],
  };
};

const handleForbiddenMsg = (message: Message, msgList: Message[]) => {
  const clientId = tryParse(message.content)?.clientId;
  if (clientId) {
    useMessageStore.getState().updateMessageStatus(clientId as string, {
      riskStatus: RiskStatus.Failed,
      status: MessageStatus.Failed,
    });
  }
  // 考虑消息是否被引用了，若坐席刚收到立马引用，消息也会存在引用消息内
  msgList.forEach((item) => {
    if (item.quoteMessage && item.quoteMessage.clientId === clientId) {
      item.quoteMessage = {
        ...item.quoteMessage,
        content: "",
        riskStatus: RiskStatus.Failed,
        type: MessageType.ForbiddenMsg,
      };
    }
  });
};

export const handleIncomingMessage = (message: Message, msgList: Message[]) => {
  message.isNewMsg = true;
  if (isForbiddenMsg(message)) {
    handleForbiddenMsg(message, msgList);
    return;
  }
  // 接收撤回消息
  if (isWithdrawnMsg(message)) {
    const recallId = tryParse(message.content)?.id;
    if (!recallId) {
      return;
    }
    // 更新本地状态
    useMessageStore.getState().withdrawMessage(recallId, message.senderType);

    // 判断当前消息是否有被引用，若被引用，则需要清除内容
    return;
  }
  if (isLineUpMsg(message)) {
    reportScene(ReportScene.LINE_UP, message.content);
  }
  if (message.type === MessageType.ConnectionSuccess) {
    message.hide = true;
    if (
      typeof window !== "undefined" &&
      window.globalData &&
      typeof window.globalData.serveSocketPreparedCallback === "function"
    ) {
      window.globalData.serveSocketPreparedCallback();
    }
  }
  const valid = isInEnum(message.type, MessageType);
  if (!valid) {
    // 如果不在枚举定义的类型范围内，则隐藏
    message.hide = true;
  }
  return message;
};

// 将换行符转换为 HTML 换行标签，同时保持文本安全
const formatContent = (content: string) => {
  // 先进行 HTML 转义以防止 XSS
  const escapedContent = content
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;");

  // 将换行符转换为 <br> 标签
  return escapedContent.replace(/\n/g, "<br>");
};

export const transferSendContent = (content: string) => {
  const parseContent = tryParse(content);
  if (!parseContent || !parseContent.text) {
    return content;
  }
  parseContent.text = formatContent(parseContent.text);
  return JSON.stringify(parseContent);
};
